import {
  ApiOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  SafetyOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { AppPaths } from "@p0security/shared/routes/constants";
import { Avatar, Button, Popover, Space, Typography } from "antd";
import { useParams } from "react-router";
import {
  type ChipTone,
  color,
  font,
  iconTile,
  motion,
  numericStyle,
} from "theme/aiInventory";

const initialsFromEmail = (email: string): string => {
  const local = email.split("@")[0] ?? "";
  const segments = local.split(".").filter(Boolean);
  if (segments.length >= 2) {
    return (segments[0][0] + segments[1][0]).toUpperCase();
  }
  return (local.slice(0, 2) || "??").toUpperCase();
};

export type SidecarHeaderStats = {
  agentName: string;
  status: string;
  ownerEmail?: string;
  connectors:
    | { kind: "connectors"; count: number; servers: number }
    | { kind: "servers"; servers: number };
  /** Names of connectors, shown in the Connectors chip hover popover. */
  connectorList?: ReadonlyArray<{ name: string; managed: boolean }>;
  risks: { high: number; total: number };
  policies: { enabled: number; total: number };
  /** Policies governing this agent, shown in the Policies chip hover popover. */
  policyList?: ReadonlyArray<{ id: string; name: string; enabled: boolean }>;
};

export type SidecarHeaderProps = {
  stats: SidecarHeaderStats;
  onClose: () => void;
};

type ChipProps = {
  label: string;
  value: React.ReactNode;
  /** 0-based ordinal — drives the staggered enter animation. */
  index: number;
  /** When true, render value with mono tabular-nums. */
  numeric?: boolean;
  icon?: React.ReactNode;
  /** Tile color for the kicker icon. */
  tone?: ChipTone;
  /** Optional node rendered next to the value (e.g. avatar). */
  leading?: React.ReactNode;
  /** When set, hovering the chip shows this content in a popover. */
  popoverContent?: React.ReactNode;
};

const kickerStyle: React.CSSProperties = {
  fontFamily: font.mono,
  fontSize: 9,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: color.inkMute,
  display: "flex",
  alignItems: "center",
  gap: 6,
};

const Chip: React.FC<ChipProps> = ({
  label,
  value,
  index,
  numeric,
  icon,
  tone = "neutral",
  leading,
  popoverContent,
}) => {
  const body = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        alignItems: "flex-start",
        paddingRight: 12,
        borderRight: `1px solid ${color.rule}`,
        minWidth: 140,
        opacity: 0,
        animation: `p0-chip-enter 320ms ${motion.base.split(" ").slice(1).join(" ")} forwards`,
        animationDelay: `${40 * index}ms`,
        cursor: popoverContent ? "default" : undefined,
      }}
    >
      <span style={kickerStyle}>
        {icon && <span style={iconTile(tone)}>{icon}</span>}
        {label}
      </span>
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: 12,
          fontWeight: 500,
          color: color.ink,
          ...(numeric ? numericStyle : null),
        }}
      >
        {leading}
        {value}
      </span>
    </div>
  );
  return popoverContent ? (
    <Popover
      placement="bottom"
      trigger="hover"
      mouseEnterDelay={0.15}
      content={popoverContent}
    >
      {body}
    </Popover>
  ) : (
    body
  );
};

const ConnectorList: React.FC<{
  items: ReadonlyArray<{ name: string; managed: boolean }>;
}> = ({ items }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      gap: 4,
      minWidth: 220,
      maxHeight: 320,
      overflow: "auto",
    }}
  >
    {items.map((c) => (
      <div
        key={c.name}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          padding: "4px 8px",
          borderRadius: 4,
          fontSize: 12,
          color: color.ink,
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {c.name}
        </span>
        <span
          style={{
            fontSize: 10,
            padding: "2px 6px",
            borderRadius: 3,
            background: c.managed ? color.integrationBg : color.unmanagedBg,
            color: c.managed ? color.integration : color.unmanaged,
            whiteSpace: "nowrap",
            fontWeight: 500,
          }}
        >
          {c.managed ? "Managed" : "Unmanaged"}
        </span>
      </div>
    ))}
  </div>
);

const PolicyList: React.FC<{
  items: ReadonlyArray<{ id: string; name: string; enabled: boolean }>;
}> = ({ items }) => {
  const { orgSlug } = useParams();
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        minWidth: 260,
        maxHeight: 320,
        overflow: "auto",
      }}
    >
      {items.map((p) => {
        const content = (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              padding: "4px 8px",
              borderRadius: 4,
              fontSize: 12,
              color: color.ink,
            }}
          >
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {p.name}
            </span>
            <span
              style={{
                fontSize: 10,
                padding: "2px 6px",
                borderRadius: 3,
                background: p.enabled ? color.integrationBg : color.mute,
                color: p.enabled ? color.integration : color.inkMute,
                whiteSpace: "nowrap",
                fontWeight: 500,
              }}
            >
              {p.enabled ? "Enabled" : "Disabled"}
            </span>
          </div>
        );
        return orgSlug ? (
          <a
            key={p.id}
            href={`/o/${orgSlug}/${AppPaths.Policies}/edit/${p.id}`}
            style={{ textDecoration: "none" }}
          >
            {content}
          </a>
        ) : (
          <div key={p.id}>{content}</div>
        );
      })}
    </div>
  );
};

const formatCount = (count: number, singular: string): React.ReactNode => (
  <>
    <strong>{count}</strong> {count === 1 ? singular : `${singular}s`}
  </>
);

const formatConnectors = (
  stats: SidecarHeaderStats["connectors"]
): React.ReactNode => {
  if (stats.kind === "servers") {
    if (stats.servers === 0) return "None";
    return formatCount(stats.servers, "server");
  }

  if (stats.count === 0) return "None";
  return (
    <Space
      split={<Typography.Text type="secondary">{"\u00B7"}</Typography.Text>}
    >
      <span>{formatCount(stats.count, "connector")}</span>
      <span>{formatCount(stats.servers, "server")}</span>
    </Space>
  );
};

const formatRisks = (high: number, total: number): React.ReactNode => {
  if (total === 0) return "None";
  return (
    <>
      <strong>{total}</strong> Total ({high} High)
    </>
  );
};

const formatPolicies = (enabled: number, total: number): React.ReactNode => {
  if (total === 0) return "None";
  return (
    <>
      <strong>
        {enabled} / {total}
      </strong>{" "}
      enabled
    </>
  );
};

export const SidecarHeader: React.FC<SidecarHeaderProps> = ({
  stats,
  onClose,
}) => (
  <Space direction="vertical" size={14} style={{ width: "100%" }}>
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 12,
        width: "100%",
      }}
    >
      <Typography.Title level={4} style={{ margin: 0, color: color.ink }}>
        {stats.agentName}
      </Typography.Title>
      <Button
        type="text"
        icon={<CloseOutlined />}
        onClick={onClose}
        aria-label="Close"
      />
    </div>
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 16,
        paddingTop: 10,
        borderTop: `1px solid ${color.rule}`,
      }}
    >
      <Chip
        index={0}
        label="Status"
        value={<strong>{stats.status}</strong>}
        icon={<CheckCircleOutlined />}
        tone="green"
      />
      <Chip
        index={1}
        label="Owner"
        value={stats.ownerEmail ?? "\u2014"}
        icon={<SafetyOutlined />}
        leading={
          stats.ownerEmail ? (
            <Avatar size={20} style={{ fontSize: 10, fontWeight: 600 }}>
              {initialsFromEmail(stats.ownerEmail)}
            </Avatar>
          ) : undefined
        }
      />
      <Chip
        index={2}
        numeric
        label={stats.connectors.kind === "servers" ? "Servers" : "Connectors"}
        value={formatConnectors(stats.connectors)}
        icon={<ApiOutlined />}
        tone="amber"
        popoverContent={
          stats.connectors.kind === "connectors" &&
          stats.connectorList &&
          stats.connectorList.length > 0 ? (
            <ConnectorList items={stats.connectorList} />
          ) : undefined
        }
      />
      <Chip
        index={3}
        numeric
        label="Risks"
        value={formatRisks(stats.risks.high, stats.risks.total)}
        icon={<WarningOutlined />}
        tone="red"
      />
      <Chip
        index={4}
        numeric
        label="Policies"
        value={formatPolicies(stats.policies.enabled, stats.policies.total)}
        icon={<SafetyOutlined />}
        tone="blue"
        popoverContent={
          stats.policyList && stats.policyList.length > 0 ? (
            <PolicyList items={stats.policyList} />
          ) : undefined
        }
      />
    </div>
  </Space>
);
